/**
 * =============================================================================
 * HOVER TOOLTIP COMPONENT
 * =============================================================================
 * 
 * A minimal, borderless tooltip that appears on hover.
 * Used for action hints throughout the application.
 * 
 * Features:
 * - No borders, subtle background
 * - Small text, muted styling
 * - Configurable position
 * - Smooth fade transition
 */

import { Tooltip, type TooltipProps } from '@mantine/core';
import { memo, type ReactNode } from 'react';

// =============================================================================
// TYPES
// =============================================================================
export interface HoverTooltipProps {
    /** The tooltip text to display */
    label: string;
    /** The element that triggers the tooltip */
    children: ReactNode;
    /** Position of the tooltip relative to the trigger */
    position?: TooltipProps['position'];
    /** Whether the tooltip is disabled */
    disabled?: boolean;
    /** Additional className for the wrapper */
    className?: string;
}

// =============================================================================
// STYLES - Button-like tooltip (light blue background, dark text)
// =============================================================================
const tooltipStyles = {
    tooltip: {
        backgroundColor: '#F0F7FF', // light blue
        color: 'rgb(55, 65, 81)', // gray-700
        fontSize: 12,
        fontWeight: 500,
        padding: '6px 12px',
        borderRadius: 8,
        border: '1px solid rgb(201, 224, 255)', // light blue border
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
} as const;

// =============================================================================
// COMPONENT
// =============================================================================
const HoverTooltip: React.FC<HoverTooltipProps> = memo(({
    label,
    children,
    position = 'bottom',
    disabled = false,
    className,
}) => {
    return (
        <Tooltip
            label={label}
            position={position}
            disabled={disabled}
            withArrow={false}
            transitionProps={{ transition: 'fade', duration: 150 }}
            styles={tooltipStyles}
            withinPortal
        >
            <span className={className} style={{ display: 'inline-flex' }}>
                {children}
            </span>
        </Tooltip>
    );
});

HoverTooltip.displayName = 'HoverTooltip';

export default HoverTooltip;
